import React, { useState, useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { Building2, Save, X, Loader2, AlertTriangle } from 'lucide-react';
import { useToast } from './Toast';
import Modal from './Modal';
import { useDocumentStore } from '../services/store';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const CompanySettings: React.FC = () => {
    const { user } = useUser();
    const { getToken } = useAuth();
    const { showToast } = useToast();
    const { setCompanySettings } = useDocumentStore();
    const [isLoading, setIsLoading] = useState(false);

    // Delete Account State
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);

    // Form State
    const [companyName, setCompanyName] = useState('');
    const [logoUrl, setLogoUrl] = useState('');

    // Edit State
    const [isEditingName, setIsEditingName] = useState(false);
    const [isSavingName, setIsSavingName] = useState(false);
    const [isSavingLogo, setIsSavingLogo] = useState(false);

    // Fetch existing settings
    useEffect(() => {
        if (!user) return;
        const fetchSettings = async () => {
            const cached = sessionStorage.getItem(`userSettings_${user.id}`);
            if (cached) {
                try {
                    const data = JSON.parse(cached);
                    if (data.company_name) setCompanyName(data.company_name);
                    if (data.company_logo_url) setLogoUrl(data.company_logo_url);
                } catch (e) { /* ignore parse error */ }
            } else {
                setIsLoading(true);
            }

            try {
                // Pass email to ensure user creation
                const email = user.primaryEmailAddress?.emailAddress;
                const url = email ? `${API_URL}/user?email=${encodeURIComponent(email)}` : `${API_URL}/user`;
                const token = await getToken();
                const res = await fetch(url, {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.company_name) setCompanyName(data.company_name);
                    if (data.company_logo_url) setLogoUrl(data.company_logo_url);

                    // Sync Cache
                    sessionStorage.setItem(`userSettings_${user.id}`, JSON.stringify({
                        company_name: data.company_name,
                        company_logo_url: data.company_logo_url
                    }));
                }
            } catch (error) {
                console.error('Failed to load settings:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchSettings();
    }, [user, getToken]);

    const handleSaveName = async () => {
        if (!user) return;
        setIsSavingName(true);
        try {
            const email = user.primaryEmailAddress?.emailAddress;
            const token = await getToken();
            const res = await fetch(`${API_URL}/user`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    company_name: companyName,
                    email // Pass email for auto-creation
                })
            });
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                console.error('Save failed:', res.status, errorData);
                throw new Error(errorData.error || 'Failed to update settings');
            }

            // Update cache
            sessionStorage.setItem(`userSettings_${user.id}`, JSON.stringify({
                company_name: companyName,
                company_logo_url: logoUrl
            }));

            // Optimistic update global store
            setCompanySettings(companyName, logoUrl);

            showToast('Company name updated', 'success');
            setIsEditingName(false);
        } catch {
            showToast('Failed to update name', 'error');
        } finally {
            setIsSavingName(false);
        }
    };

    const handleDeleteAccount = async () => {
        if (deleteConfirmation !== 'DELETE') {
            showToast('Please type DELETE exactly to confirm.', 'error');
            return;
        }
        if (!user) return;

        setIsDeletingAccount(true);
        try {
            await user.delete();
            // Clerk auto-redirects/logs out upon successful deletion
            showToast('Account properly deleted.', 'success');
        } catch (error) {
            console.error('Failed to delete account:', error);
            showToast('Failed to delete account. Please contact support.', 'error');
        } finally {
            setIsDeletingAccount(false);
            setIsDeleteDialogOpen(false);
        }
    };

    const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!user || !e.target.files?.[0]) return;

        const file = e.target.files[0];
        if (file.size > 500 * 1024) { // 500KB limit
            showToast('Logo must be under 500KB', 'error');
            return;
        }

        setIsSavingLogo(true);

        // Convert to Base64
        const reader = new FileReader();
        reader.onload = async (ev) => {
            if (ev.target?.result) {
                const newLogoUrl = ev.target.result as string;
                try {
                    const email = user.primaryEmailAddress?.emailAddress;
                    const token = await getToken();
                    const res = await fetch(`${API_URL}/user`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                        },
                        body: JSON.stringify({
                            company_logo_url: newLogoUrl,
                            email // Pass email for auto-creation
                        })
                    });

                    if (!res.ok) {
                        const errorData = await res.json().catch(() => ({}));
                        console.error('Logo upload failed:', res.status, errorData);
                        throw new Error(errorData.error || 'Failed to update settings');
                    }

                    // Update cache
                    sessionStorage.setItem(`userSettings_${user.id}`, JSON.stringify({
                        company_name: companyName,
                        company_logo_url: newLogoUrl
                    }));

                    // Optimistic update global store
                    setCompanySettings(companyName, newLogoUrl);

                    setLogoUrl(newLogoUrl);
                    showToast('Logo updated successfully', 'success');
                } catch (error) {
                    console.error(error);
                    showToast('Failed to upload logo', 'error');
                } finally {
                    setIsSavingLogo(false);
                }
            }
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveLogo = async () => {
        if (!user) return;
        if (!confirm('Are you sure you want to remove the logo?')) return;

        setIsSavingLogo(true);
        try {
            const token = await getToken();
            await fetch(`${API_URL}/user`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({ company_logo_url: '' })
            });

            // Update cache
            sessionStorage.setItem(`userSettings_${user.id}`, JSON.stringify({
                company_name: companyName,
                company_logo_url: ''
            }));

            // Optimistic update global store
            setCompanySettings(companyName, null);

            setLogoUrl('');
            showToast('Logo removed', 'success');
        } catch {
            showToast('Failed to remove logo', 'error');
        } finally {
            setIsSavingLogo(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center p-8">
                <Loader2 className="animate-spin text-slate-400" />
            </div>
        );
    }

    return (
        <div className="w-full max-w-3xl">
            <div className="mb-6 pb-4 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-900">Company details</h2>
                <p className="text-sm text-slate-500 mt-1">Manage branding for exports.</p>
            </div>

            <div className="space-y-0">
                {/* Company Name Row */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-6 border-b border-slate-200 gap-4">
                    <div className="sm:w-1/3">
                        <label className="text-sm font-semibold text-slate-900">Company Name</label>
                    </div>

                    <div className="flex-1 w-full sm:w-auto">
                        {isEditingName ? (
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    className="w-full px-3 py-1.5 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none"
                                    placeholder="Enter company name"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    autoFocus
                                />
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={handleSaveName}
                                        disabled={isSavingName}
                                        className="p-1.5 bg-slate-900 text-white rounded hover:bg-slate-800 disabled:opacity-50"
                                    >
                                        {isSavingName ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                    </button>
                                    <button
                                        onClick={() => setIsEditingName(false)}
                                        className="p-1.5 text-slate-500 hover:text-slate-800"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-between w-full">
                                <span className={companyName ? "text-sm text-slate-900" : "text-sm text-slate-400 italic"}>
                                    {companyName || 'Not set'}
                                </span>
                            </div>
                        )}
                    </div>

                    {!isEditingName && (
                        <div className="sm:w-auto">
                            <button
                                onClick={() => setIsEditingName(true)}
                                className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors"
                            >
                                Update
                            </button>
                        </div>
                    )}
                </div>

                {/* Company Logo Row */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-6 border-b border-slate-200 gap-4">
                    <div className="sm:w-1/3">
                        <label className="text-sm font-semibold text-slate-900">Company Logo</label>
                    </div>

                    <div className="flex-1 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                            {logoUrl ? (
                                <img src={logoUrl} alt="Logo" className="w-full h-full object-contain p-1" />
                            ) : (
                                <Building2 className="text-slate-400" size={18} />
                            )}
                        </div>
                        <div className="flex flex-col">
                            <span className="text-sm font-medium text-slate-900">
                                {logoUrl ? 'Logo uploaded' : 'No logo set'}
                            </span>
                        </div>
                    </div>

                    <div className="sm:w-auto flex items-center gap-3">
                        {logoUrl && (
                            <button
                                onClick={handleRemoveLogo}
                                disabled={isSavingLogo}
                                className="text-sm font-medium text-red-500 hover:text-red-700 transition-colors"
                            >
                                Remove
                            </button>
                        )}

                        <div className="relative">
                            <input
                                type="file"
                                id="logo-upload-row"
                                className="hidden"
                                accept="image/*"
                                onChange={handleLogoSelect}
                                disabled={isSavingLogo}
                            />
                            <label
                                htmlFor="logo-upload-row"
                                className={`text-sm font-medium ${isSavingLogo ? 'text-slate-300' : 'text-slate-500 hover:text-slate-900'} cursor-pointer transition-colors`}
                            >
                                {isSavingLogo ? 'Uploading...' : (logoUrl ? 'Change logo' : 'Upload logo')}
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* Danger Zone */}
            <div className="mt-12 pt-6 border-t border-red-200">
                <h2 className="text-xl font-bold text-red-600 mb-1 flex items-center gap-2">
                    <AlertTriangle size={20} /> Danger Zone
                </h2>
                <p className="text-sm text-slate-500 mb-6">Actions here are permanent and cannot be undone.</p>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-6 border border-red-100 bg-red-50/50 px-5 rounded-xl">
                    <div className="sm:w-2/3">
                        <label className="text-sm font-semibold text-slate-900 block mb-1">Delete Account</label>
                        <p className="text-sm text-slate-600">
                            Permanently delete your account, your data, and instantly cancel any active subscriptions.
                        </p>
                    </div>
                    <div className="mt-4 sm:mt-0 sm:w-auto shrink-0 flex items-center justify-end w-full sm:w-auto">
                        <button
                            onClick={() => {
                                setDeleteConfirmation('');
                                setIsDeleteDialogOpen(true);
                            }}
                            className="bg-white border border-red-200 text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg text-sm font-semibold shadow-sm transition-colors active:scale-95 whitespace-nowrap"
                        >
                            Delete Account
                        </button>
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isDeleteDialogOpen}
                onClose={() => !isDeletingAccount && setIsDeleteDialogOpen(false)}
                title="Delete Account"
                type="danger"
                cancelText="Cancel"
                confirmText={isDeletingAccount ? "Deleting..." : "Permanently Delete Account"}
                onConfirm={handleDeleteAccount}
                isLoading={isDeletingAccount}
            >
                <div>
                    <p className="text-slate-600 mb-4 leading-relaxed">
                        This action is <strong className="text-red-600">irreversible</strong>. Your account, documents, export templates, and active subscriptions will be entirely destroyed.
                    </p>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                        Type <strong className="text-slate-900 select-all">DELETE</strong> below to confirm:
                    </label>
                    <input
                        type="text"
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none font-mono tracking-widest text-lg transition-all shadow-sm"
                        placeholder="DELETE"
                        autoCapitalize="characters"
                        disabled={isDeletingAccount}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default CompanySettings;

